import React, { useEffect, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "../../state/hooks";
import { setScreenFormData } from "../../state/slices/formsSlice";
import { store } from "../../state/store";
import { apiService } from "../../services/api";
import HrmStatCards from "./HrmStatCards";
import { getResolvedEmployeeId, getUserRole } from "../../utils/visibilityUtils";
import {
  monthYearToDateRange,
  resolveAttendanceScope,
} from "../../utils/attendanceScreenConfig";

type CardConfig = {
  name?: string;
  bindValue?: string;
  icon?: React.ReactNode;
  defaultValue?: number;
  valueFormat?: string;
  color?: string;
  description?: string;
};

interface InfoCardsWithStatsProps {
  screenId: string;
  infoCardsConfig: {
    apiname?: string;
    cards?: CardConfig[];
    variant?: "default" | "compact";
  };
  formData: Record<string, any>;
}

function normalizeHrmsInfoCardsPath(apiname: string | undefined): string | null {
  if (!apiname || typeof apiname !== "string") return null;
  const trimmed = apiname.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith("/pabws/")) return trimmed;
  if (trimmed.startsWith("/g360/")) return `/pabws${trimmed.slice("/g360".length)}`;
  if (trimmed.startsWith("/hrms/")) return `/pabws${trimmed}`;
  return trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
}

function defaultCalendarMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

/**
 * HRMS stat cards: GET `apiname` (normalized to /pabws/...) and merge bindValue fields into screen formData.
 */
const InfoCardsWithStats: React.FC<InfoCardsWithStatsProps> = ({
  screenId,
  infoCardsConfig,
  formData,
}) => {
  const dispatch = useAppDispatch();
  const authUser = useAppSelector((state) => state.auth?.user);
  const apiname = infoCardsConfig.apiname;
  const cards = infoCardsConfig.cards ?? [];
  const cardBindKeys = useMemo(() => {
    const c = infoCardsConfig.cards ?? [];
    return c.map((x) => x.bindValue || x.name || "").join("|");
  }, [infoCardsConfig.cards]);

  const employeeId =
    authUser?.employee_id !== undefined && authUser?.employee_id !== null
      ? String(authUser.employee_id).trim()
      : getResolvedEmployeeId();

  const attendanceMonth =
    String(formData?.month_year ?? formData?.attendance_month_filter ?? "").trim() ||
    defaultCalendarMonth();

  useEffect(() => {
    const path = normalizeHrmsInfoCardsPath(apiname);
    if (!path) return;

    let cancelled = false;
    const payrollStatsScope = String(formData?.payroll_stats_scope || "month").toLowerCase();

    if (path.includes("payroll/statistics") && payrollStatsScope === "selection") {
      return;
    }

    if (path.includes("payroll/statistics")) {
      const resetPatch: Record<string, unknown> = {};
      for (const card of infoCardsConfig.cards ?? []) {
        const key = card.bindValue;
        if (!key) continue;
        resetPatch[key] =
          card.defaultValue ??
          (key === "total_payout" ? "0.00" : key === "employees_processed" ? "0/0" : 0);
      }
      if (Object.keys(resetPatch).length > 0) {
        const current = store.getState().forms.formDataByScreen[screenId] || {};
        dispatch(
          setScreenFormData({
            screenId,
            data: { ...current, ...resetPatch },
          })
        );
      }
    }

    const run = async () => {
      try {
        const query: Record<string, string> = {};
        if (path.includes("payroll/statistics")) {
          const ym =
            String(formData?.payroll_month_filter || "").trim() || defaultCalendarMonth();
          query.payroll_month = ym;
        }
        if (path.includes("attendance/statistics")) {
          const roleId = getUserRole();
          const adminTab = String(formData?.attendance_admin_tab || "my").toLowerCase();
          const scope = resolveAttendanceScope(
            roleId,
            adminTab === "team" ? "team" : "my"
          );
          const { start_date, end_date } = monthYearToDateRange(attendanceMonth);
          query.start_date = start_date;
          query.end_date = end_date;
          query.scope = scope;

          if (scope === "my" && employeeId) {
            query.employee_id = employeeId;
          }
          if (scope === "team" && authUser?.branch_id) {
            query.branch_id = String(authUser.branch_id);
          }
        }

        const stats = await apiService.get<Record<string, unknown>>(
          path,
          Object.keys(query).length ? query : undefined,
          {
            includeAuthToken: true,
            authHeaderType: "Bearer",
            errorHandling: "return-null",
          }
        );
        if (cancelled || !stats || typeof stats !== "object") return;

        const payload = stats as Record<string, any>;
        const dataPayload =
          payload.data && typeof payload.data === "object" && !Array.isArray(payload.data)
            ? (payload.data as Record<string, any>)
            : payload;

        const keys = (infoCardsConfig.cards ?? [])
          .map((c) => c.bindValue)
          .filter(Boolean) as string[];
        const patch: Record<string, any> = {};
        for (const k of keys) {
          if (dataPayload[k] !== undefined && dataPayload[k] !== null) {
            patch[k] = dataPayload[k];
          }
        }
        if (path.includes("payroll/statistics")) {
          patch.payroll_stats_scope = "month";
        }
        if (Object.keys(patch).length === 0) return;

        const current = store.getState().forms.formDataByScreen[screenId] || {};
        dispatch(
          setScreenFormData({
            screenId,
            data: { ...current, ...patch },
          })
        );
      } catch {
        /* ignore */
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [
    apiname,
    screenId,
    dispatch,
    cardBindKeys,
    formData?.payroll_month_filter,
    formData?.payroll_stats_scope,
    formData?.attendance_admin_tab,
    attendanceMonth,
    employeeId,
    authUser?.branch_id,
  ]);

  const stats = cards.map((card: CardConfig) => {
    const key = card.bindValue || card.name || "";
    const value =
      formData[key] !== undefined && formData[key] !== null && formData[key] !== ""
        ? formData[key]
        : card.defaultValue ?? 0;

    return {
      id: key || card.name || "card",
      label: card.name || key,
      value,
      icon: card.icon,
      accentColor: card.color || "#3B82F6",
      description: card.description || "",
    };
  });

  return <HrmStatCards stats={stats} variant={infoCardsConfig.variant || "default"} />;
};

export default InfoCardsWithStats;
